import { mkdir, rm, stat, writeFile } from "node:fs/promises";
import { relative, resolve } from "node:path";
import { scanRoutes } from "../router/route-scanner.js";
import type { Adapter } from "./index.js";
import { writeSsrEntry } from "./shared.js";
import { DEFAULT_CAPABILITIES } from "../runtime/capabilities.js";

/**
 * Bun adapter for nix-js-kit.
 *
 * Produces a self-contained Bun server entry at `.nix-js/bun-server.ts`.
 * Run it with:
 *
 *   bun run .nix-js/bun-server.ts
 *
 * The server serves static files from `dist/` and renders pages on demand for
 * unmatched routes.
 */
export const bunAdapter: Adapter = {
  name: "bun",
  capabilities: DEFAULT_CAPABILITIES,

  async build(options) {
    const root = resolve(options.root);
    const outDir = resolve(root, options.outDir);
    const generatedDir = resolve(root, ".nix-js");

    // Verify the production build exists.
    try {
      await stat(outDir);
    } catch {
      throw new Error(
        `Output directory not found: ${outDir}. Run "nix-js-kit build" first.`,
      );
    }

    // Clean previous adapter output.
    await rm(generatedDir, { recursive: true, force: true });
    await mkdir(generatedDir, { recursive: true });

    // Scan routes and generate a self-contained SSR handler entry.
    const appDir = resolve(root, options.appDir);
    const routes = await scanRoutes(appDir);

    const entryPath = resolve(generatedDir, "bun-index.ts");
    await writeSsrEntry(entryPath, routes, options);

    // Write the Bun server entry.
    const serverPath = resolve(generatedDir, "bun-server.ts");
    await writeFile(
      serverPath,
      buildBunServerSource(relativeToUrlPath(generatedDir, outDir), options),
      "utf8",
    );
  },
};

function buildBunServerSource(
  outDirUrl: string,
  options: {
    clientEntry: string;
    lang: string;
    port?: number;
  },
): string {
  return `// AUTO-GENERATED by @deijose/nix-js-kit. Do not edit.
import { fileURLToPath } from "node:url";
import { createWebHandler } from "@deijose/nix-js-kit/runtime";
import handler from "./bun-index.ts";

const outDir = fileURLToPath(new URL(${JSON.stringify(outDirUrl)}, import.meta.url));
const port = Number(process.env.PORT) || ${options.port ?? 3000};

const webHandler = createWebHandler(
  { pages: [], api: [], error404: undefined, error500: undefined },
  {},
  { staticRoot: outDir, lang: ${JSON.stringify(options.lang)}, clientEntry: ${JSON.stringify(options.clientEntry)} },
);

Bun.serve({
  port,
  async fetch(request) {
    // Try static files first via the unified handler, then fall back to the
    // bundled SSR handler for dynamic routes.
    let response = await webHandler(request);
    if (response.status === 404) {
      response = await handler(request);
    }
    return response;
  },
});

console.log(\`Bun server running at http://localhost:\${port}\`);
`;
}

function relativeToUrlPath(from: string, to: string): string {
  const path = relative(from, to).split("\\").join("/");
  return `${path.startsWith(".") ? path : `./${path}`}/`;
}
